import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import Modal from '../components/Modal';
import {
  ActionBar,
  Badge,
  Button,
  FormGrid,
  FormRow,
  Input,
  Label,
  Muted,
  Page,
  PageHeader,
  PageHeaderMain,
  PanelGrid,
  Select,
  Subtitle,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  SurfaceTitle,
  Table,
  TableWrap,
  TextArea,
  Title,
  Toast,
  Toolbar,
} from '../components/ui';

interface AlertTypeConfig {
  type: string;
  label: string;
  enabled: boolean;
  imageUrl: string;
  soundUrl: string;
  durationMs: number;
  durationSeconds?: number;
  volume: number;
  textTemplate: string;
  textSize: number;
  textAlign?: 'left' | 'center' | 'right';
  layout: string;
  minAmount: number;
  filter: string;
}

const LAYOUT_OPTIONS = [
  { value: 'text_bottom_image_top', label: 'Imagen arriba · texto abajo' },
  { value: 'text_top_image_bottom', label: 'Texto arriba · imagen abajo' },
  { value: 'text_over_image', label: 'Texto sobre imagen' },
  { value: 'text_left_image_right', label: 'Texto izquierda · imagen derecha' },
  { value: 'text_right_image_left', label: 'Texto derecha · imagen izquierda' },
];

const TEXT_ALIGN_OPTIONS = [
  { value: 'left', label: 'Izquierda' },
  { value: 'center', label: 'Centrado' },
  { value: 'right', label: 'Derecha' },
];

interface OverlayUrls {
  global: string;
  byType: Record<string, string>;
}

interface SettingsResponse {
  settings: { overlayKey: string; width: number; height: number };
  urls: OverlayUrls;
  types: AlertTypeConfig[];
}

const OBS_CSS = `body {
  background-color: rgba(0, 0, 0, 0);
  margin: 0px auto;
  overflow: hidden;
}`;

const UrlRow: React.FC<{ label: string; value: string; onCopy: (v: string) => void }> = ({ label, value, onCopy }) => (
  <FormRow>
    <Label>{label}</Label>
    <Toolbar style={{ gap: '0.45rem' }}>
      <Input readOnly value={value} style={{ flex: 1, minWidth: 0 }} />
      <Button $size="sm" type="button" onClick={() => onCopy(value)}>Copiar</Button>
    </Toolbar>
  </FormRow>
);

const AlertsPage: React.FC = () => {
  const [data, setData] = useState<SettingsResponse | null>(null);
  const [width, setWidth] = useState('800');
  const [height, setHeight] = useState('600');
  const [editing, setEditing] = useState<AlertTypeConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    const { data: res } = await api.get<SettingsResponse>('/alerts/settings');
    setData(res);
    setWidth(String(res.settings.width));
    setHeight(String(res.settings.height));
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyText = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setMessage('Copiado al portapapeles');
    setTimeout(() => setMessage(''), 2500);
  };

  const saveDimensions = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.put('/alerts/settings', {
        width: parseInt(width, 10),
        height: parseInt(height, 10),
      });
      setData((prev) => prev ? { ...prev, settings: res.settings, urls: res.urls } : prev);
      setMessage('Dimensiones guardadas');
    } finally {
      setSaving(false);
    }
  };

  const regenerateKey = async () => {
    if (!window.confirm('¿Regenerar clave? Las URLs viejas en OBS dejarán de funcionar.')) return;
    const { data: res } = await api.put('/alerts/settings', { regenerateKey: true });
    setData((prev) => prev ? { ...prev, settings: res.settings, urls: res.urls } : prev);
    setMessage('Nueva clave generada — actualizá OBS');
  };

  const toggleType = async (type: AlertTypeConfig) => {
    await api.put(`/alerts/types/${type.type}`, { enabled: !type.enabled });
    load();
  };

  const saveType = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    await api.put(`/alerts/types/${editing.type}`, {
      label: editing.label,
      enabled: editing.enabled,
      textTemplate: editing.textTemplate,
      durationSeconds: editing.durationSeconds ?? Math.round((editing.durationMs || 5000) / 1000),
      volume: editing.volume,
      minAmount: editing.minAmount,
      textSize: editing.textSize,
      textAlign: editing.textAlign || 'center',
      layout: editing.layout,
    });
    setEditing(null);
    load();
    setMessage('Alerta guardada');
  };

  const uploadImage = async (type: string, file: File | null) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('image', file);
    await api.post(`/alerts/types/${type}/image`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    load();
    if (editing?.type === type) {
      const { data: fresh } = await api.get<SettingsResponse>('/alerts/settings');
      const updated = fresh.types.find((t) => t.type === type);
      if (updated) setEditing(updated);
    }
  };

  const uploadSound = async (type: string, file: File | null) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('sound', file);
    await api.post(`/alerts/types/${type}/sound`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    load();
    if (editing?.type === type) {
      const { data: fresh } = await api.get<SettingsResponse>('/alerts/settings');
      const updated = fresh.types.find((t) => t.type === type);
      if (updated) setEditing(updated);
    }
  };

  const testAlert = async (type: string) => {
    try {
      await api.post(`/alerts/test/${type}`);
      setMessage('Alerta de prueba enviada — mirá OBS si tenés el overlay abierto');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setMessage(msg || 'No se pudo probar');
    }
  };

  if (!data) {
    return (
      <Page>
        <Title>Alertas OBS</Title>
        <Muted>Cargando...</Muted>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <PageHeaderMain>
          <Title>Alertas OBS</Title>
          <Subtitle>
            Configurá imágenes y sonidos. Pegá las URLs en OBS como fuente Navegador (fondo transparente).
          </Subtitle>
        </PageHeaderMain>
      </PageHeader>

      {message && <Toast>{message}</Toast>}

      <PanelGrid $cols="minmax(0, 1.05fr) minmax(0, 1fr)">
        <Surface>
          <SurfaceHeader>
            <SurfaceTitle>URLs para OBS</SurfaceTitle>
          </SurfaceHeader>
          <SurfaceBody>
            <UrlRow label="Global (todas las alertas activas)" value={data.urls.global} onCopy={copyText} />
            <UrlRow label="Solo puja en subasta" value={data.urls.byType.auction_bid || ''} onCopy={copyText} />
            {Object.entries(data.urls.byType)
              .filter(([type]) => type !== 'auction_bid')
              .map(([type, url]) => {
                const label = data.types.find((t) => t.type === type)?.label || type;
                return (
                  <UrlRow key={type} label={`Solo ${label.toLowerCase()}`} value={url} onCopy={copyText} />
                );
              })}
            <FormRow>
              <Label>Dimensiones recomendadas OBS</Label>
              <Toolbar>
                <Input type="number" value={width} onChange={(e) => setWidth(e.target.value)} placeholder="Ancho" style={{ maxWidth: 110 }} />
                <Input type="number" value={height} onChange={(e) => setHeight(e.target.value)} placeholder="Alto" style={{ maxWidth: 110 }} />
                <Button $size="sm" type="button" onClick={saveDimensions} disabled={saving}>Guardar</Button>
              </Toolbar>
            </FormRow>
            <FormRow>
              <Button $variant="ghost" $size="sm" type="button" onClick={regenerateKey}>Regenerar clave overlay</Button>
            </FormRow>
            <FormRow>
              <Label>CSS personalizado OBS</Label>
              <TextArea readOnly value={OBS_CSS} rows={4} />
              <Button $size="sm" type="button" style={{ marginTop: '0.45rem' }} onClick={() => copyText(OBS_CSS)}>Copiar CSS</Button>
            </FormRow>
          </SurfaceBody>
        </Surface>

        <Surface>
          <SurfaceHeader>
            <SurfaceTitle>Tipos de alerta</SurfaceTitle>
          </SurfaceHeader>
          <SurfaceBody $flush>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th style={{ width: '12%' }}>Estado</th>
                    <th style={{ width: '42%', textAlign: 'right' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {data.types.map((t) => (
                    <tr key={t.type}>
                      <td>{t.label}</td>
                      <td><Badge $active={t.enabled}>{t.enabled ? 'Activa' : 'Off'}</Badge></td>
                      <td style={{ textAlign: 'right' }}>
                        <ActionBar>
                          <Button $variant="ghost" $size="sm" type="button" onClick={() => setEditing({ ...t })}>Editar</Button>
                          <Button $variant="ghost" $size="sm" type="button" onClick={() => toggleType(t)}>
                            {t.enabled ? 'Off' : 'On'}
                          </Button>
                          <Button $size="sm" type="button" onClick={() => testAlert(t.type)}>Probar</Button>
                        </ActionBar>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
            <div style={{ padding: '0.65rem 1rem' }}>
              <Muted style={{ margin: 0 }}>
                Follow, sub, raid y bits están listos para configurar; se conectarán a Twitch en una próxima fase.
              </Muted>
            </div>
          </SurfaceBody>
        </Surface>
      </PanelGrid>

      <Modal
        open={!!editing}
        title={editing ? `Editar: ${editing.label}` : 'Editar alerta'}
        onClose={() => setEditing(null)}
        wide
        footer={editing ? (
          <>
            <Button $variant="ghost" type="button" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button type="button" onClick={() => testAlert(editing.type)}>Probar en OBS</Button>
            <Button type="submit" form="alert-edit-form">Guardar</Button>
          </>
        ) : undefined}
      >
        {editing && (
          <form id="alert-edit-form" onSubmit={saveType}>
            <FormGrid>
              <FormRow>
                <Label>Nombre visible</Label>
                <Input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} />
              </FormRow>
              <FormRow style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: '#b8a9c9', fontSize: '0.84rem' }}>
                  <input
                    type="checkbox"
                    checked={editing.enabled}
                    onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
                  />
                  Activada
                </label>
              </FormRow>
              <FormRow style={{ gridColumn: '1 / -1' }}>
                <Label>Texto en pantalla</Label>
                <TextArea
                  value={editing.textTemplate}
                  onChange={(e) => setEditing({ ...editing, textTemplate: e.target.value })}
                  rows={3}
                />
                <Muted>
                  Variables: {'{displayName}'}, {'{twitchUsername}'}, {'{amount}'}, {'{auctionTitle}'}.
                  Enter = salto de línea en OBS.
                </Muted>
              </FormRow>
              <FormRow>
                <Label>Tamaño de texto (px)</Label>
                <Input
                  type="number"
                  min={12}
                  max={72}
                  value={editing.textSize ?? 24}
                  onChange={(e) => setEditing({ ...editing, textSize: parseInt(e.target.value, 10) || 24 })}
                />
              </FormRow>
              <FormRow>
                <Label>Alineación del texto</Label>
                <Select
                  value={editing.textAlign || 'center'}
                  onChange={(e) => setEditing({
                    ...editing,
                    textAlign: e.target.value as AlertTypeConfig['textAlign'],
                  })}
                >
                  {TEXT_ALIGN_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Select>
              </FormRow>
              <FormRow>
                <Label>Disposición imagen / texto</Label>
                <Select
                  value={editing.layout || 'text_bottom_image_top'}
                  onChange={(e) => setEditing({ ...editing, layout: e.target.value })}
                >
                  {LAYOUT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Select>
              </FormRow>
              <FormRow>
                <Label>Duración visible (segundos)</Label>
                <Input
                  type="number"
                  min={1}
                  max={60}
                  value={editing.durationSeconds ?? Math.round((editing.durationMs || 5000) / 1000)}
                  onChange={(e) => setEditing({
                    ...editing,
                    durationSeconds: parseInt(e.target.value, 10) || 5,
                    durationMs: (parseInt(e.target.value, 10) || 5) * 1000,
                  })}
                />
              </FormRow>
              <FormRow>
                <Label>Volumen (0-100)</Label>
                <Input
                  type="number"
                  value={editing.volume}
                  onChange={(e) => setEditing({ ...editing, volume: parseInt(e.target.value, 10) || 80 })}
                />
              </FormRow>
              {(editing.type === 'auction_bid' || editing.type === 'auction_end') && (
                <FormRow style={{ gridColumn: '1 / -1' }}>
                  <Label>Monto mínimo de puja para alertar (ARS)</Label>
                  <Input
                    type="number"
                    value={editing.minAmount}
                    onChange={(e) => setEditing({ ...editing, minAmount: parseInt(e.target.value, 10) || 0 })}
                  />
                  <Muted>Independiente de la puja mínima configurada en cada subasta.</Muted>
                </FormRow>
              )}
              <FormRow>
                <Label>Imagen / GIF</Label>
                {editing.imageUrl && (
                  <img src={editing.imageUrl} alt="" style={{ maxHeight: 72, marginBottom: '0.45rem', display: 'block', borderRadius: 8 }} />
                )}
                <Input type="file" accept="image/*" onChange={(e) => uploadImage(editing.type, e.target.files?.[0] || null)} />
              </FormRow>
              <FormRow>
                <Label>Sonido (mp3, wav, ogg)</Label>
                {editing.soundUrl && (
                  <audio controls src={editing.soundUrl} style={{ width: '100%', marginBottom: '0.45rem' }} />
                )}
                <Input type="file" accept="audio/*" onChange={(e) => uploadSound(editing.type, e.target.files?.[0] || null)} />
              </FormRow>
            </FormGrid>
          </form>
        )}
      </Modal>
    </Page>
  );
};

export default AlertsPage;
